import React from "react";
import { useTranslation } from "react-i18next";
import { renderConnectionStrength } from "../../grid/CellRenderer";

interface ColumnDataPreviewProps {
  columnName: string;
  data: string[];
  maxItems?: number;
  fieldId?: string | null;
}

// Format numeric values for display (like CellRenderer.tsx)
const formatDisplayValue = (value: string, fieldId?: string | null): string | React.ReactNode => {
  if (!value) return value;

  // Check if it's a numeric value
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return value;

  // Connection strength field - render as colored badge
  if (fieldId === "connectionStrength") {
    return renderConnectionStrength(numValue);
  }

  // Only format if it looks like a plain number (not a date, phone, etc.)
  if (!value.match(/^-?\d+(\.\d+)?$/)) return value;

  // Format with thousand separators, remove unnecessary .0 decimals
  return numValue % 1 === 0
    ? Math.round(numValue).toLocaleString()
    : numValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const ColumnDataPreview: React.FC<ColumnDataPreviewProps> = ({
  columnName,
  data,
  maxItems = 10,
  fieldId,
}) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return (
      <div className="w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-semibold text-gray-900">{columnName}</span>
          <span className="text-xs text-gray-500">{t("Data preview")}</span>
        </div>
        <div className="text-sm text-gray-500 italic">{t("Empty")}</div>
      </div>
    );
  }

  const displayData = data.slice(0, maxItems);
  const hasMore = data.length > maxItems;

  return (
    <div className="w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-semibold text-gray-900">{columnName}</span>
        <span className="text-xs text-gray-500">{t("Data preview")}</span>
      </div>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {displayData.map((value, index) => (
          <div
            key={index}
            className="text-sm text-gray-700 py-1 px-2 rounded hover:bg-gray-50"
            style={{ wordBreak: "break-word" }}
          >
            {value ? (
              formatDisplayValue(value, fieldId)
            ) : (
              <span className="text-gray-400 italic">(empty)</span>
            )}
          </div>
        ))}
      </div>
      {hasMore && (
        <div className="mt-2 text-xs text-gray-500 italic">
          {t("This preview shows only a portion of the column values")}
        </div>
      )}
    </div>
  );
};

export default ColumnDataPreview;
